import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";
import {
    Autocomplete, Chip, CircularProgress, IconButton, InputAdornment, Paper, Tab, TableCell,
    TableRow, Tabs, TextField, Tooltip, Typography, createFilterOptions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import DirectionsBusFilledOutlinedIcon from "@mui/icons-material/DirectionsBusFilledOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import TableLayout from "../../Ui/TableLayout";
import { HeadCell } from "../../../Objects/interfaces/TableTypes";
import DocumentRepository, { DocumentInfo } from "../../../Repositories/DocumentRepository";
import TransportSubscriptionRepository, { TransportSubscriptionInfo } from "../../../Repositories/TransportSubscriptionRepository";
import TransportSubscriptionUpload, { SubscriptionClientOption } from "../../../App/DeclarationCourse/Travel/TransportSubscriptionUpload";
import TeachrRepository from "../../../Repositories/TeachrRepository";
import { resolveClientRowFields } from "../../../App/Clients/clientDetailNavigation";
import ButtonField from "../../Ui/ButtonField";
import { BrandContent, useTailwindBrand } from "../../shared";
import { capitalizeFirstLetter } from "../../../Utils/Function";
import { ENV_TEACHR, getActiveClientBrandConfig, isBrandActive, isDepositEnabled } from "../../../config/environment.config";
import { EXPIRY_ALERT_DAYS, statusOf, transportPeriod } from "./documentStatus";

interface Props {
    variant: "teachr" | "client";
    ownerId: number;
    /**
     * Vue prof uniquement : filtre fixe sur un client (fiche client). La liste
     * est demandée au serveur avec ?client=, la colonne et le filtre Client
     * sont masqués, ainsi que le bandeau d'abonnement transport.
     */
    clientId?: number;
    /** Rendu compact (sans bandeau d'abonnement), intégrable dans une section de page. */
    embedded?: boolean;
}

/** Option du filtre Client, dérivée des documents chargés. */
interface ClientOption {
    id: number;
    name: string;
    /** Élèves rattachés à ce client dans les documents (hors homonyme du client). */
    children: string[];
}

const TYPE_CONFIG: Record<DocumentInfo["type"], { label: string; icon: JSX.Element }> = {
    quote: { label: "Devis", icon: <DescriptionOutlinedIcon fontSize="small" /> },
    invoice: { label: "Facture", icon: <ReceiptLongOutlinedIcon fontSize="small" /> },
    invoice_recap: { label: "Facture", icon: <SummarizeOutlinedIcon fontSize="small" /> },
    travel_attestation: { label: "Attestation de déplacement", icon: <DirectionsCarFilledOutlinedIcon fontSize="small" /> },
    deposit_attestation: { label: "Attestation d'acompte", icon: <HistoryEduOutlinedIcon fontSize="small" /> },
    tax_credit: { label: "Attestation fiscale", icon: <AccountBalanceOutlinedIcon fontSize="small" /> },
    transport: { label: "Abonnement transport", icon: <DirectionsBusFilledOutlinedIcon fontSize="small" /> },
};

/** Catégories d'affichage : les récapitulatifs comptent comme des factures. */
const CATEGORY_LABELS: Record<string, string> = {
    quote: "Devis",
    invoice: "Factures",
    attestation: "Attestations",
    tax_credit: "Attestations fiscales",
    transport: "Abonnements transport",
};

/**
 * Catégorie à présélectionner, passée dans le state de navigation
 * (documentsCategory, ex. "transport" depuis une notification d'abonnement).
 * Une valeur absente ou inconnue est ignorée.
 */
const requestedCategoryOf = (state: unknown): string | null => {
    const value = (state as { documentsCategory?: unknown } | null)?.documentsCategory;
    return typeof value === "string" && Object.prototype.hasOwnProperty.call(CATEGORY_LABELS, value) ? value : null;
};

const categoryOf = (doc: DocumentInfo): string => {
    if (doc.category) return doc.category;
    if (doc.type === "invoice" || doc.type === "invoice_recap") return "invoice";
    if (doc.type === "travel_attestation") return "attestation";
    return doc.type;
};

/** Normalise pour la recherche : minuscules et sans accents. */
const normalize = (value: string): string =>
    value.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/** L'élève porte-t-il le même nom que le client (client qui prend ses propres cours) ? */
const sameName = (a: string, b: string): boolean => normalize(a).trim() === normalize(b).trim();

/** Nom de l'élève à afficher sous celui du client, s'il apporte une information. */
const childNameOf = (doc: DocumentInfo): string | null =>
    doc.childName && !(doc.clientName && sameName(doc.childName, doc.clientName)) ? doc.childName : null;

/** Colonnes : la colonne Client n'existe que dans la vue prof non filtrée sur un client. */
const headCellsFor = (withClient: boolean): HeadCell[] => {
    const cells: Omit<HeadCell, "id">[] = [
        { label: "Document", disable: false, numeric: false },
        ...(withClient ? [{ label: capitalizeFirstLetter(getActiveClientBrandConfig()), disable: false, numeric: false }] : []),
        { label: "Catégorie", disable: false, numeric: false },
        { label: "Date", disable: false, numeric: false },
        { label: "Statut", disable: false, numeric: false },
        // Pas de colonne Montant : le montant qui fait foi est celui du document lui-même
        // (devis, facture, attestation), les lectures net / TTC différaient selon la marque.
        { label: "Télécharger", disable: false, numeric: false, align: "center" },
    ];
    return cells.map((cell, index) => ({ ...cell, id: index + 1 }));
};

/** Le filtre Client cherche aussi dans les noms des élèves (accents et casse ignorés). */
const clientFilterOptions = createFilterOptions<ClientOption>({
    stringify: (option) => `${option.name} ${option.children.join(" ")}`,
});

/**
 * P3 : espace documentaire unifié. Documents du professeur ou du client
 * regroupés par catégorie (onglets), avec recherche (document, client,
 * élève), filtre par client côté prof, filtres par période et pagination,
 * sur le même modèle que les listes d'élèves et de cours (TableLayout).
 */
const DocumentSpace = ({ variant, ownerId, clientId, embedded = false }: Props) => {
    const { classes, common, brandConfig } = useTailwindBrand();
    const location = useLocation();
    // Seul l'espace complet (page Mes documents) suit le state de navigation
    const followsLocation = !embedded && !(variant === "teachr" && clientId !== undefined);
    const requestedCategory = followsLocation ? requestedCategoryOf(location.state) : null;
    const [loading, setLoading] = useState(true);
    const [documents, setDocuments] = useState<DocumentInfo[]>([]);
    const [category, setCategory] = useState<string>(() => requestedCategory ?? "all");
    const [nameSearch, setNameSearch] = useState<string>("");
    const [clientFilter, setClientFilter] = useState<ClientOption | null>(null);
    const [fromFilter, setFromFilter] = useState<string>("");
    const [toFilter, setToFilter] = useState<string>("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(embedded ? 5 : 10);
    // P6 : abonnement de transports en commun du prof (bandeau + dépôt)
    const [currentSubscription, setCurrentSubscription] = useState<TransportSubscriptionInfo | null>(null);
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [subscriptionRefresh, setSubscriptionRefresh] = useState(0);
    const [confirmingId, setConfirmingId] = useState<number | null>(null);
    // Dépôt depuis l'espace documents (hors fiche client) : le prof choisit le
    // client concerné parmi ses clients validés.
    const [subscriptionClients, setSubscriptionClients] = useState<SubscriptionClientOption[]>([]);

    const openSubscriptionModal = async () => {
        setSubscriptionModalOpen(true);
        if (subscriptionClients.length > 0) return;
        try {
            const response: any = await TeachrRepository.getAllClientsForTeachr({
                teachrId: ownerId, archive: 0, page: 1, variable: 0, sort: 1, limit: 500, validated: true,
            } as any);
            const rows: any[] = response?.data ?? [];
            const options = rows
                .map((row) => {
                    const { clientId: id, fullName } = resolveClientRowFields(row);
                    return id ? { id: Number(id), name: fullName } : null;
                })
                .filter((o): o is SubscriptionClientOption => o !== null);
            const unique = Array.from(new Map(options.map((o) => [o.id, o])).values())
                .sort((a, b) => a.name.localeCompare(b.name, "fr"));
            setSubscriptionClients(unique);
        } catch (e) {
            console.warn("Liste des clients indisponible pour le dépôt d'abonnement", e);
        }
    };

    // Nouvelle navigation demandant une catégorie alors que l'espace est déjà affiché
    // (notification cliquée depuis cette rubrique) : l'onglet suit, filtres levés pour
    // qu'ils ne le masquent pas. Sans demande, rien ne change.
    const [syncedLocationKey, setSyncedLocationKey] = useState(location.key);
    if (location.key !== syncedLocationKey) {
        setSyncedLocationKey(location.key);
        if (requestedCategory) {
            setCategory(requestedCategory);
            setNameSearch("");
            setClientFilter(null);
            setFromFilter("");
            setToFilter("");
            setPage(0);
        }
    }

    const isTeachr = variant === "teachr";
    const hasFixedClient = isTeachr && clientId !== undefined;
    const showClientColumn = isTeachr && !hasFixedClient;
    const showSubscriptionBanner = isTeachr && !hasFixedClient && !embedded;
    const studentIcon = isBrandActive(ENV_TEACHR) ? "🎓" : "👤";

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const fetchDocuments = variant === "teachr"
            ? DocumentRepository.getTeachrDocuments(ownerId, clientId !== undefined ? { client: clientId } : undefined)
            : DocumentRepository.getClientDocuments(ownerId);
        fetchDocuments
            .then((response) => {
                if (!cancelled) setDocuments(response?.documents ?? []);
            })
            .catch(() => {
                if (!cancelled) setDocuments([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [variant, ownerId, clientId, subscriptionRefresh]);

    useEffect(() => {
        if (variant !== "teachr" || clientId !== undefined || embedded) return undefined;
        let cancelled = false;
        TransportSubscriptionRepository.list(ownerId)
            .then((response) => {
                if (!cancelled) setCurrentSubscription(response?.current ?? null);
            })
            .catch(() => {
                if (!cancelled) setCurrentSubscription(null);
            });
        return () => {
            cancelled = true;
        };
    }, [variant, ownerId, clientId, embedded, subscriptionRefresh]);

    // Clients distincts présents dans les documents : aucun appel API, et
    // jamais une option qui donnerait une liste vide.
    const clientOptions = useMemo<ClientOption[]>(() => {
        if (!showClientColumn) return [];
        const byId = new Map<number, ClientOption>();
        documents.forEach((doc) => {
            if (doc.clientId == null || !doc.clientName) return;
            const option = byId.get(doc.clientId) ?? { id: doc.clientId, name: doc.clientName, children: [] };
            const child = childNameOf(doc);
            if (child && !option.children.includes(child)) option.children.push(child);
            byId.set(doc.clientId, option);
        });
        return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, "fr"));
    }, [documents, showClientColumn]);

    // Le filtre Client délimite l'espace : onglets et compteurs portent sur ce sous-ensemble.
    const scopedDocuments = useMemo(
        () => documents
            // Verified Trainer : pas d'acompte, donc pas d'attestation d'acompte
            .filter((doc) => isDepositEnabled() || doc.type !== "deposit_attestation")
            .filter((doc) => !clientFilter || doc.clientId === clientFilter.id),
        [documents, clientFilter]
    );

    const availableCategories = useMemo(
        () => Object.keys(CATEGORY_LABELS).filter((key) => scopedDocuments.some((doc) => categoryOf(doc) === key)),
        [scopedDocuments]
    );

    // Un client choisi n'a ni abonnement transport ni attestation fiscale :
    // si l'onglet actif se vide, retour sur « Tous » (dès ce rendu, puis
    // dans l'état pour ne pas y revenir quand le filtre est levé).
    const activeCategory = category === "all" || availableCategories.includes(category) ? category : "all";
    useEffect(() => {
        if (!loading && category !== "all" && !availableCategories.includes(category)) {
            setCategory("all");
            setPage(0);
        }
    }, [loading, category, availableCategories]);

    // Client disparu des documents après un rechargement : filtre levé.
    useEffect(() => {
        if (!loading && clientFilter && !clientOptions.some((option) => option.id === clientFilter.id)) {
            setClientFilter(null);
        }
    }, [loading, clientFilter, clientOptions]);

    const filtered = useMemo(() => {
        const search = normalize(nameSearch.trim());
        const from = fromFilter ? moment(fromFilter, "YYYY-MM-DD").startOf("day") : null;
        const to = toFilter ? moment(toFilter, "YYYY-MM-DD").endOf("day") : null;
        return scopedDocuments.filter((doc) => {
            if (activeCategory !== "all" && categoryOf(doc) !== activeCategory) return false;
            if (search && !normalize(`${doc.label} ${doc.number ?? ""} ${doc.clientName ?? ""} ${doc.childName ?? ""}`).includes(search)) return false;
            // Un abonnement couvre une période : il est retenu dès qu'elle
            // chevauche les dates choisies. Les autres documents ont une date.
            const start = doc.validFrom ?? doc.date;
            const end = doc.validUntil ? moment(doc.validUntil, "YYYY-MM-DD").endOf("day") : (doc.date ? moment(doc.date) : null);
            if (from && (!end || end.isBefore(from))) return false;
            if (to && (!start || moment(start).isAfter(to))) return false;
            return true;
        });
    }, [scopedDocuments, activeCategory, nameSearch, fromFilter, toFilter]);

    const paginated = useMemo(
        () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filtered, page, rowsPerPage]
    );

    const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };
    const clearFilters = () => {
        setCategory("all");
        setNameSearch("");
        setClientFilter(null);
        setFromFilter("");
        setToFilter("");
        setPage(0);
    };

    if (loading) {
        return (
            <div className={`flex items-center gap-3 ${embedded ? "p-3" : "p-6"}`}>
                <CircularProgress size={20} />
                <BrandContent
                    tag="p"
                    children={<>{hasFixedClient ? "Chargement des documents…" : "Chargement de vos documents…"}</>}
                    type="description"
                    className={common.body}
                />
            </div>
        );
    }

    const renderClientCell = (doc: DocumentInfo) => {
        if (doc.clientName) {
            const child = childNameOf(doc);
            return (
                <>
                    <Typography variant="body2" fontWeight={600} lineHeight={1.2} sx={{ fontFamily: brandConfig.fontFamily }}>
                        {doc.clientName}
                    </Typography>
                    {child && (
                        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2} sx={{ fontFamily: brandConfig.fontFamily }}>
                            {studentIcon} {child}
                        </Typography>
                    )}
                </>
            );
        }
        if (doc.type === "transport") {
            return (
                <Tooltip title={`Justificatif personnel, rattaché à aucun ${getActiveClientBrandConfig()}`}>
                    <span className="italic text-gray-400">Document personnel</span>
                </Tooltip>
            );
        }
        // undefined : backend antérieur à l'ajout du client dans les documents
        return <span className="text-gray-400">{doc.clientName === undefined ? "…" : "Non renseigné"}</span>;
    };

    const renderDateCell = (doc: DocumentInfo) => {
        const period = doc.type === "transport" ? transportPeriod(doc) : null;
        if (period) return capitalizeFirstLetter(period);
        return doc.date ? capitalizeFirstLetter(moment(doc.date).format("DD MMM YYYY")) : "…";
    };

    // Abonnement « à confirmer » (adresse modifiée) : confirmation en un clic
    // depuis la liste, l'abonnement redevient une preuve valable.
    const confirmSubscription = async (doc: DocumentInfo) => {
        if (doc.subscriptionId == null) return;
        setConfirmingId(doc.subscriptionId);
        try {
            await TransportSubscriptionRepository.confirm(doc.subscriptionId);
            setSubscriptionRefresh((v) => v + 1);
        } catch (e) {
            console.warn("Confirmation de l'abonnement impossible", e);
        } finally {
            setConfirmingId(null);
        }
    };

    const renderStatusCell = (doc: DocumentInfo) => {
        const status = statusOf(doc);
        if (!status) return null;
        return (
            <span className="inline-flex flex-wrap items-center gap-1">
                <Tooltip title={status.tooltip ?? ""}>
                    <Chip
                        label={status.label}
                        size="small"
                        color={status.color}
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.7rem", fontFamily: brandConfig.fontFamily }}
                    />
                </Tooltip>
                {variant === "teachr" && doc.type === "transport" && doc.status === "to_confirm" && doc.subscriptionId != null && (
                    <ButtonField
                        size="small"
                        variant="outlined"
                        label="Confirmer"
                        loading={confirmingId === doc.subscriptionId}
                        disabled={confirmingId !== null}
                        onClick={() => confirmSubscription(doc)}
                    />
                )}
            </span>
        );
    };

    const renderRow = (doc: DocumentInfo, index: number) => (
        <TableRow key={doc.id ?? `${doc.type}-${doc.number ?? doc.label}-${index}`} hover>
            <TableCell sx={{ fontFamily: brandConfig.fontFamily }}>
                <span className="flex items-center gap-2">
                    <span className={classes.primaryText}>{TYPE_CONFIG[doc.type]?.icon}</span>
                    {doc.label}
                </span>
            </TableCell>
            {showClientColumn && (
                <TableCell sx={{ fontFamily: brandConfig.fontFamily }}>
                    {renderClientCell(doc)}
                </TableCell>
            )}
            <TableCell sx={{ fontFamily: brandConfig.fontFamily }}>
                {CATEGORY_LABELS[categoryOf(doc)] ?? TYPE_CONFIG[doc.type]?.label ?? doc.type}
            </TableCell>
            <TableCell sx={{ fontFamily: brandConfig.fontFamily }}>
                {renderDateCell(doc)}
            </TableCell>
            <TableCell sx={{ fontFamily: brandConfig.fontFamily }}>
                {renderStatusCell(doc)}
            </TableCell>
            <TableCell align="center">
                <Tooltip title="Ouvrir le document">
                    <IconButton
                        size="small"
                        onClick={() => window.open(doc.url, "_blank", "noopener")}
                        sx={{ color: brandConfig.colors.primary }}
                    >
                        <FileDownloadOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    );

    return (
        <div className={`w-full ${embedded ? "space-y-3" : "space-y-4"}`}>
            {/* P6 : justificatif d'abonnement de transports en commun du prof */}
            {showSubscriptionBanner && (
                <div
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl p-4 border"
                    style={{
                        backgroundColor: currentSubscription && !currentSubscription.isExpired
                            ? `${brandConfig.colors?.primary ?? "#16a34a"}08`
                            : "#fffbeb",
                        borderColor: currentSubscription && !currentSubscription.isExpired
                            ? `${brandConfig.colors?.primary ?? "#16a34a"}30`
                            : "#fde68a",
                    }}
                >
                    <div className={brandConfig.fontFamily}>
                        {currentSubscription && !currentSubscription.isExpired ? (
                            <>
                                <p className={`font-bold ${classes.primaryText}`}>
                                    🚌 Abonnement {currentSubscription.typeLabel}
                                    {currentSubscription.clientName ? ` pour ${currentSubscription.clientName}` : ""}, valide jusqu'au {moment(currentSubscription.validUntil).format("DD/MM/YYYY")}
                                </p>
                                <p className="text-sm text-gray-600">
                                    {currentSubscription.expiresInDays <= EXPIRY_ALERT_DAYS
                                        ? `Il expire dans ${currentSubscription.expiresInDays} jour${currentSubscription.expiresInDays > 1 ? "s" : ""} : pensez à déposer le nouveau.`
                                        : `Il sert automatiquement de preuve de déplacement pour vos déclarations couvertes${currentSubscription.clientName ? ` avec ${currentSubscription.clientName}` : ""}. Un abonnement se dépose par ${getActiveClientBrandConfig("", "élève", "client")}.`}
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="font-bold text-amber-900">🚌 Aucun abonnement de transports en commun en cours</p>
                                <p className="text-sm text-amber-800">
                                    Déposez votre abonnement (Navigo, TER...) pour {getActiveClientBrandConfig("", "un élève", "un client")} : il servira de preuve de déplacement
                                    pour toutes vos déclarations avec lui tant qu'il est valide.
                                </p>
                            </>
                        )}
                    </div>
                    <ButtonField
                        label={currentSubscription && !currentSubscription.isExpired ? "Déposer un autre abonnement" : "Déposer mon abonnement"}
                        variant={currentSubscription && !currentSubscription.isExpired ? "outlined" : "contained"}
                        className="font-bold"
                        onClick={openSubscriptionModal}
                    />
                </div>
            )}
            {subscriptionModalOpen && (
                <TransportSubscriptionUpload
                    open
                    teachr={{ id: ownerId } as any}
                    clientOptions={subscriptionClients}
                    onClose={() => setSubscriptionModalOpen(false)}
                    onSaved={() => {
                        setSubscriptionModalOpen(false);
                        setSubscriptionRefresh((v) => v + 1);
                    }}
                />
            )}

            {/* Onglets par catégorie */}
            <Tabs
                value={activeCategory}
                onChange={(_, newValue) => { setCategory(newValue); setPage(0); }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                    minHeight: 40,
                    "& .MuiTab-root": { fontFamily: brandConfig.fontFamily, textTransform: "none", fontWeight: 600, minHeight: 40 },
                    "& .Mui-selected": { color: `${brandConfig.colors.primary} !important` },
                    "& .MuiTabs-indicator": { backgroundColor: brandConfig.colors.primary },
                }}
            >
                <Tab value="all" label={`Tous (${scopedDocuments.length})`} />
                {availableCategories.map((key) => (
                    <Tab
                        key={key}
                        value={key}
                        label={`${CATEGORY_LABELS[key]} (${scopedDocuments.filter((doc) => categoryOf(doc) === key).length})`}
                    />
                ))}
            </Tabs>

            {/* Recherche, filtre client et filtres par période */}
            <div className="flex flex-wrap items-end gap-3">
                <TextField
                    size="small"
                    label="Rechercher"
                    placeholder={showClientColumn ? `Numéro, nom du document, ${getActiveClientBrandConfig()}…` : "Numéro, nom du document…"}
                    value={nameSearch}
                    onChange={(e) => { setNameSearch(e.target.value); setPage(0); }}
                    sx={{ minWidth: embedded ? 220 : 260, backgroundColor: "white", fontFamily: brandConfig.fontFamily }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                {showClientColumn && clientOptions.length > 0 && (
                    <Autocomplete
                        size="small"
                        options={clientOptions}
                        value={clientFilter}
                        onChange={(_, value) => { setClientFilter(value); setPage(0); }}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        filterOptions={clientFilterOptions}
                        noOptionsText={`Aucun ${getActiveClientBrandConfig()} trouvé`}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                <span className="flex flex-col">
                                    <span>{option.name}</span>
                                    {option.children.length > 0 && (
                                        <span className="text-xs text-gray-500">{studentIcon} {option.children.join(", ")}</span>
                                    )}
                                </span>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={capitalizeFirstLetter(getActiveClientBrandConfig())}
                                placeholder={`Tous les ${getActiveClientBrandConfig("s")}`}
                            />
                        )}
                        sx={{ minWidth: 240, backgroundColor: "white", fontFamily: brandConfig.fontFamily }}
                    />
                )}
                <TextField
                    size="small"
                    type="date"
                    label="Du"
                    value={fromFilter}
                    onChange={(e) => { setFromFilter(e.target.value); setPage(0); }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ backgroundColor: "white" }}
                />
                <TextField
                    size="small"
                    type="date"
                    label="Au"
                    value={toFilter}
                    onChange={(e) => { setToFilter(e.target.value); setPage(0); }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ backgroundColor: "white" }}
                />
                {(activeCategory !== "all" || nameSearch || clientFilter || fromFilter || toFilter) && (
                    <Tooltip title="Effacer tous les filtres">
                        <IconButton onClick={clearFilters} size="small" color="error">
                            <ClearIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                <BrandContent
                    tag="p"
                    children={<>{filtered.length} document{filtered.length > 1 ? "s" : ""}</>}
                    type="description"
                    className={`ml-auto font-semibold ${common.body}`}
                />
            </div>

            {filtered.length > 0 ? (
                <TableLayout
                    Children={paginated.map((doc, index) => renderRow(doc, index))}
                    headCells={headCellsFor(showClientColumn)}
                    page={page + 1}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    rowsPerPage={rowsPerPage}
                    count={filtered.length}
                    size="small"
                    stickyHeader
                    paperSx={{ boxShadow: embedded ? 1 : 2, borderRadius: 2 }}
                />
            ) : (
                <Paper
                    elevation={0}
                    sx={{ height: embedded ? 160 : 260, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", borderRadius: 2, border: "1px dashed", borderColor: "divider", bgcolor: "grey.50", gap: 1, px: 2 }}
                >
                    <Typography fontSize={embedded ? "2rem" : "2.5rem"}>📁</Typography>
                    <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: 360, fontFamily: brandConfig.fontFamily }}>
                        {documents.length === 0
                            ? hasFixedClient
                                ? `Aucun document pour le moment avec ${getActiveClientBrandConfig("", "cet ", "ce ")} : ses devis, factures et attestations de déplacement apparaîtront ici.`
                                : "Aucun document pour le moment. Vos devis, factures et attestations apparaîtront ici au fil de vos déclarations."
                            : "Aucun document ne correspond aux filtres sélectionnés."}
                    </Typography>
                </Paper>
            )}
        </div>
    );
};

export default DocumentSpace;
